import type { OptionStockObject, OptionStockObjectInput } from "@/lib/gql/__generated__/graphql";

import { stockRemainNumZodSchema } from "@/components/features/_shared/validation-schema/validation-schema";

// Action の型定義
type ActionValue = {
	optionID: string;
	subOptionID: string | undefined;
	date: string;
	isAvailable: boolean;
	remainNum: number;
};

export class OptionStockDiffStore {
	#diffState: ActionValue[] = [];

	constructor(
		private readonly optionStockObjects: OptionStockObject[], //
		private readonly onSetIsValid: (isValid: boolean) => void,
		private readonly onSetIsDirty: (isDirty: boolean) => void,
	) {}

	/**
	 * 更新のあったデータを追加
	 * @param value
	 */
	readonly put = (value: ActionValue) => {
		this.#diffState = this.#diffState.filter((stateItem) => !this.#isSameActionValue(stateItem, value));
		this.#diffState.push(value);

		// render中に状態更新処理が走る場合があるので、遅延させている
		setTimeout(() => {
			this.onSetIsValid(this.isValid());
			this.onSetIsDirty(this.isDirty());
		}, 0);
	};

	readonly clear = () => {
		this.#diffState.length = 0;
	};

	readonly isDirty = () => {
		if (!this.#diffState.length) return false;

		const diffItems = this.#diffState;
		const optionStockObjects = this.optionStockObjects;

		const isDirty = diffItems.some((diffItem) => {
			const optionStockObject = optionStockObjects.find((optionStockObject) => {
				if (diffItem.subOptionID) {
					return (
						optionStockObject.optionID === diffItem.optionID &&
						optionStockObject.subOptionID === diffItem.subOptionID
					);
				}

				return optionStockObject.optionID === diffItem.optionID;
			});

			if (!optionStockObject) return false;

			const sameStockItem = optionStockObject.stocks.find((stock) => stock.date === diffItem.date);

			if (!sameStockItem) return false;

			return sameStockItem.remainNum !== diffItem.remainNum || sameStockItem.isAvailable !== diffItem.isAvailable;
		});

		return isDirty;
	};

	readonly isValid = () => {
		return this.#diffState.every((item) => stockRemainNumZodSchema.safeParse(item.remainNum).success);
	};

	readonly getCurrentOptionStock = (value: ActionValue) => {
		return this.#diffState.find((item) => this.#isSameActionValue(item, value));
	};

	/**
	 * サーバーに送信するデータを取得
	 * @returns
	 */
	readonly getDiffItems = (): Array<OptionStockObjectInput> => {
		const optionIDs = this.#diffState.map((item) => item.optionID);
		const notDuplicateOptionIDs = Array.from(new Set(optionIDs));

		const diffItems = notDuplicateOptionIDs
			.map((optionID) => {
				const subOptionIDs = this.#diffState
					.filter((stateItem) => stateItem.optionID === optionID) // 特定のオプションにひもづくサブオプションIDを取得
					.map((item) => item.subOptionID);
				const notDuplicateSubOptionIDs = Array.from(new Set(subOptionIDs)); // 重複を排除
				return notDuplicateSubOptionIDs.map((subOptionID) => ({
					optionID,
					subOptionID,
					stocks: this.#diffState
						.filter((stateItem) => stateItem.optionID === optionID && stateItem.subOptionID === subOptionID) // 特定のオプションとサブオプションにひもづくデータを取得
						.map((item) => ({
							date: item.date,
							remainNum: item.remainNum,
							isAvailable: item.isAvailable,
						})),
				}));
			})
			.flat();

		return diffItems;
	};

	#isSameActionValue = (a: ActionValue, b: ActionValue) => {
		if (a.optionID !== b.optionID) {
			return false;
		}

		if (a.subOptionID !== b.subOptionID) {
			return false;
		}

		if (a.date !== b.date) {
			return false;
		}

		return true;
	};
}
